import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import { MAX_TERMS } from '../shared/queries.ts';
import type { IssueFilter } from '../shared/queries.ts';
import { setUser, toast, useToast, useUser } from './api.ts';
import { Activity } from './components/Activity.tsx';
import { Board } from './components/Board.tsx';
import { CreateIssue } from './components/CreateIssue.tsx';
import { Detail } from './components/Detail.tsx';
import { LiveQueries } from './components/LiveQueries.tsx';
import { IssueList } from './components/IssueList.tsx';
import { ResizeHandle, useDetailWidth } from './components/ResizeHandle.tsx';
import { Search } from './components/Search.tsx';
import { useLive, useLiveCount } from './live.ts';
import { navigate, useRoute } from './router.ts';
import type { View } from './router.ts';
import { withTerm } from './search.ts';
import type { Project, User } from './types.ts';

const VIEWS: { view: View; label: string }[] = [{ view: 'list', label: 'List' }, { view: 'board', label: 'Board' }, { view: 'activity', label: 'Activity' }];

/**
 * The project to open on before the project list has arrived — put in the HTML by the Worker
 * (`worker/index.ts`'s `withDefaultProject`), which has the argument for why it is there.
 *
 * Read once at module scope, because it is a constant of the page load and a value that cannot
 * change under a render. `null` when the Worker injected nothing, which is the old behaviour.
 */
const DEFAULT_PROJECT: string | null = (() => {
  const injected = (globalThis as { __R2DB_DEFAULT_PROJECT?: unknown }).__R2DB_DEFAULT_PROJECT;
  return typeof injected === 'string' && injected ? injected : null;
})();

export function App() {
  const route = useRoute();
  const [filter, setFilter] = useState<IssueFilter>([]);
  const projects = useLive<Project[]>('projects').data ?? [];
  // Held here rather than in `Search`, because the picker in the header needs the same list and
  // two live queries on one small table is a waste of a subscription.
  const users = useLive<User[]>('users').data ?? [];
  // The project the list and board read. The URL wins; then the id the Worker put in the HTML;
  // then the first project the switcher knows about, once it arrives. With none of the three
  // there is nothing to scope a query to and the views wait — opening an UNSCOPED one is the
  // shape the whole partition exists to prevent, so "no project yet" has to mean no query rather
  // than a query over everything.
  //
  // `DEFAULT_PROJECT` is what removes the second serial round trip. Without it this read
  // `projects[0]?.id`, so the list could not be LEASED until the project list had arrived — and
  // `projects` is the query that pays the object's cold wake, measured at 3,222 ms against the
  // 172 ms the list itself then took. It is a hint and is treated as one: once `projects` is here
  // it has the final say, so an id that is no longer in the base costs one wasted lease and a
  // swap, not an empty board.
  const fallbackProject = useMemo(() => {
    const first = projects[0]?.id ?? null;
    if (!DEFAULT_PROJECT) return first;
    if (!projects.length) return DEFAULT_PROJECT;
    return projects.some(p => p.id === DEFAULT_PROJECT) ? DEFAULT_PROJECT : first;
  }, [projects]);
  const project = route.project ?? fallbackProject;
  const open = (id: string) => navigate({ issue: id, creating: false });
  const close = () => navigate({ issue: null, creating: false });
  /**
   * A tag clicked — on a row, on a card, or in the open issue — is the `tag:` term the search box
   * would have made, added to whatever is already filtering rather than replacing it: clicking is
   * a second way into the same box, not a second kind of search.
   *
   * It lands on the view, not on the page, which is the one case worth a redirect: the activity
   * feed is cross-project and has no search box, so a tag clicked from a thread opened over it
   * would otherwise change a filter nothing on screen reads.
   */
  const searchTag = (name: string) => {
    const next = withTerm(filter, { axis: 'tag', value: name });
    if (!next) { toast(`${MAX_TERMS} terms is the most one search takes.`); return; }
    setFilter(next);
    if (route.view === 'activity') navigate({ view: 'list' });
  };
  const detailOpen = route.creating || route.issue !== null;
  const [detailWidth, setDetailWidth] = useDetailWidth();

  return (
    <>
      <Header />
      <Notice />
      <main className="shell" id="main">
        <div className="toolbar">
          <nav className="views" aria-label="Views">
            {VIEWS.map(({ view, label }) => (
              <button key={view} type="button" className={route.view === view ? 'active' : ''} aria-current={route.view === view ? 'page' : undefined} onClick={() => navigate({ view })}>{label}</button>
            ))}
          </nav>
          {route.view !== 'activity' && (
            // The switcher and the open project's size are one unit: the second is a fact about
            // what the first selected, and they share a baseline rather than each finding its own.
            <div className="project-pick">
              <ProjectPicker projects={projects} current={project} />
              <ProjectMeta projects={projects} project={project} />
            </div>
          )}
          {route.view !== 'activity' && <Search filter={filter} users={users} onChange={setFilter} />}
          <button type="button" className="button small new" onClick={() => navigate({ creating: true, issue: null })}>New issue <span aria-hidden="true">→</span></button>
        </div>
        <div className={`workspace${detailOpen ? ' with-detail' : ''}`} style={{ '--detail-width': `${detailWidth}px` } as CSSProperties}>
          <div className="view">
            {route.view === 'list' && (project
              ? <IssueList project={project} filter={filter} selectedId={route.issue} onOpen={open} onSearchTag={searchTag} />
              : <p className="empty">Opening a live query…</p>)}
            {route.view === 'board' && (project
              ? <Board project={project} filter={filter} selectedId={route.issue} onOpen={open} onSearchTag={searchTag} />
              : <p className="empty">Opening a live query…</p>)}
            {route.view === 'activity' && <Activity onOpen={open} />}
          </div>
          {detailOpen && (
            <aside className="detail" aria-label={route.creating ? 'New issue' : 'Issue'}>
              <ResizeHandle width={detailWidth} onResize={setDetailWidth} />
              {route.creating
                ? <CreateIssue project={project} projects={projects} onCancel={close} onCreated={open} />
                : <Detail key={route.issue} id={route.issue!} onClose={close} onSearchTag={searchTag} />}
            </aside>
          )}
        </div>
      </main>
      <Toast />
    </>
  );
}

/**
 * Which project the list and the board are reading.
 *
 * A `<select>` over every project rather than a typeahead, because the whole table fits: a tracker
 * has thousands of projects against millions of issues, which is why `project` is the one table a
 * query here reads all of.
 */
function ProjectPicker({ projects, current }: { projects: Project[]; current: string | null }) {
  return (
    <select
      className="project-picker" value={current ?? ''} aria-label="Project"
      disabled={!projects.length}
      onChange={e => navigate({ project: e.target.value, issue: null, creating: false })}
    >
      {!projects.length && <option value="">Loading…</option>}
      {projects.map(p => <option key={p.id} value={p.id}>{p.key} · {p.name}</option>)}
    </select>
  );
}

/**
 * The two numbers under the switcher: how many projects there are to choose, and how many issues
 * the chosen one holds. One caption, because they are one thought — the size of the choice and the
 * size of what was chosen — and because the control is the subject and these are about it. It
 * hangs under the select exactly as the axis row hangs under the search box beside it.
 *
 * The issue half is the only live query in the toolbar, and the cheapest one on the page:
 * `projectId` is prefix 1 of `issue_by_project_created`, so the engine answers it from counted
 * branch pages without reading a leaf (design 463; `issueCount`'s note has the measurements). It
 * is the project's size and so does NOT follow the search — a filtered list says what its window
 * holds, and this says what the project holds.
 *
 * It is **omitted while it is stale**, which is not the same as while it is loading: `useLive`
 * keeps the previous arguments' rows on screen until the new query settles, so a switch would
 * otherwise print the project you just left's total under the name of the one you just opened —
 * briefly here, and for as long as a cold object takes on the deployed board.
 */
function ProjectMeta({ projects, project }: { projects: Project[]; project: string | null }) {
  return (
    <p className="project-meta micro muted" aria-live="polite">
      {projects.length ? `${projects.length.toLocaleString()} PROJECTS` : 'PROJECTS'}
      {/* A child, so that the query is MOUNTED with a project rather than asked for one with no
          project pinned — which `parseArgs` refuses, as it must. */}
      {project && <IssueTotal project={project} />}
    </p>
  );
}

function IssueTotal({ project }: { project: string }) {
  const live = useLive<{ count: number }[]>('issueCount', { project });
  const total = live.stale ? undefined : live.data?.[0]?.count;
  return total === undefined ? null : <>{` · ${total.toLocaleString()} ISSUES`}</>;
}

function Header() {
  return (
    <header className="header">
      <a className="brand" href="/" onClick={e => { e.preventDefault(); navigate({ view: 'list', issue: null, creating: false }); }}>
        <img src="/brand/logo.svg" alt="" width="30" height="30" />
        <span>Issues</span>
        <span className="tag">R2DB DEMO</span>
      </a>
      <div className="header-actions">
        <UserPicker />
        <a className="text-link" href="https://r2db.dev">Built on r2db <span aria-hidden="true">→</span></a>
      </div>
    </header>
  );
}

/** Who you are, for ownership and comments. Pick one of the cast, or type any name. */
function UserPicker() {
  const user = useUser();
  const users = useLive<User[]>('users').data ?? [];
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    const next = (draft ?? '').trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    if (next) setUser(next);
    setDraft(null);
  };
  return (
    <label className="user-picker">
      <span className="micro">YOU ARE</span>
      <input
        list="cast" value={draft ?? user} aria-label="Your user name"
        onFocus={() => setDraft('')} onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); } }}
      />
      <datalist id="cast">{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</datalist>
    </label>
  );
}

const HOW_KEY = 'r2db-issues.how';
function readHowOpen(): boolean {
  try { return localStorage.getItem(HOW_KEY) !== 'closed'; } catch { return true; }
}

/** The strip under the header: what this page is, how many live queries it holds right now, and
 *  the four steps behind them — open on a first visit, and remembered once closed.
 *
 *  The count is a button, because the number is a claim and the claim is checkable: it opens the
 *  list of what those queries actually are. */
function Notice() {
  const live = useLiveCount();
  const [open, setOpen] = useState(readHowOpen);
  const [queriesOpen, setQueriesOpen] = useState(false);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(HOW_KEY, next ? 'open' : 'closed'); } catch { /* fine: it just is not remembered */ }
  };
  return (
    <>
      <div className="notice">
        <button type="button" className="live-toggle" aria-expanded={queriesOpen} onClick={() => setQueriesOpen(!queriesOpen)}>
          <span className="status-dot" /> {live} LIVE {live === 1 ? 'QUERY' : 'QUERIES'} {queriesOpen ? '↑' : '↓'}
        </button>
        <span>PUBLIC DEMO · EVERYONE SEES EVERY EDIT AS IT HAPPENS · RESETS NIGHTLY 09:00 UTC</span>
        <button type="button" className="how-toggle" aria-expanded={open} onClick={toggle}>{open ? 'HIDE HOW IT WORKS ↑' : 'HOW IT WORKS ↓'}</button>
      </div>
      {queriesOpen && <LiveQueries />}
      {open && <HowItWorks />}
    </>
  );
}

/** The numbers here are the deployed base's own build output — `README.md` quotes the
 *  `build_issues_store` run that produced them. Rebuild the base at a different size and they move. */
function HowItWorks() {
  return (
    <section className="how" aria-label="How this page works">
      <div className="how-lede">
        <h2>Open a second window<span className="accent">.</span></h2>
        <p>Every list, board column, thread and feed here is a live query on a database hosted on Object Storage.</p>
        <p>That database is 100 GiB, 27.1 million issues, 1,535,462 pages of 64 KiB. One Durable Object can serve it because almost none of that is ever resident.</p>
      </div>
      <ol className="how-steps">
        <li><span>01</span><strong>Tiers</strong><p>A page is in one of three places: this object's RAM, this object's own SQLite, or R2, which holds the published base as 23,992 immutable 4 MiB parts.</p></li>
        <li><span>02</span><strong>Resident</strong><p>What stays beside the engine is the B+tree's interior and nothing else: 1,120 branch pages, 73.4 MB, 0.073% of the store. The upper levels are pinned in RAM under a fixed byte budget; the rest sit in local storage, a synchronous row read away. A descent never blocks on the way down.</p></li>
        <li><span>03</span><strong>Fault</strong><p>A descent misses only at the leaf. A page no resident tier holds is recorded and the read unwinds; the object fetches that batch and retries. A page id <em>is</em> its byte offset in a part, so a fetch is arithmetic rather than a lookup, and adjacent leaves coalesce into one ranged GET. Rounds follow a read's dependency depth, not how many pages it touches.</p></li>
        <li><span>04</span><strong>Commit</strong><p>A write copies the pages it touches into the object's own storage, which every later read sees ahead of R2, and the rows that moved go out as a delta to each live query watching them. The base parts are never mutated — the overlay is published later as a layer and folded into a new base, which is also why resetting this board nightly costs two pointer writes instead of 100.6 GB.</p></li>
      </ol>
    </section>
  );
}

function Toast() {
  const text = useToast();
  return <div className="toast" role="status" aria-live="polite" hidden={!text}>{text}</div>;
}
